import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  beautyAvailabilityApi,
  beautyCreateAppointmentApi,
  beautyCreateSeriesApi,
  beautyListServicesApi,
} from "../../../api/organizerBeautyApi";
import { COUNTRIES, SingleDatePickerPill } from "../../../pages/reservas/components/ReservaShared";
import PhoneCountryInput from "../../../pages/reservas/components/PhoneCountryInput";
import SpinLoading from "../../../components/spinLoading/SpinLoading";
import { Chip } from "../../canchas/primerUso/clubPasos";
import { Campo } from "../../reservas/primerUso/barPasos";
import {
  idDeAdicional,
  idDeVariante,
  itemsDeSeleccion,
  mensajeDeSeleccion,
  rangoDeDuracion,
  seleccionCompleta,
  tieneAdicionales,
  tieneOpciones,
  tieneVariantes,
  totalDeEleccion,
} from "../../../helpers/beautyLineas";
import { DATE_FORMAT, duracionLabel, mensajeDe, plata, toHHmm, toMin } from "./agendaLayout";
import { pluralTurnos } from "../../reservas/components/courtSeriesModel";
import TurnoFijoBlock from "./TurnoFijoBlock";
import useTurnoFijo from "./useTurnoFijo";
import { seriesErrorMessage, skippedEntries } from "./turnoFijoModel";
import "../../events/EventForm.css";
import "../turnos-beauty.css";
import "../../primerUso/primerUso.css";
import "../../reservas/reservas-organizer.css";
import "./agenda.css";

/* ─────────────────────────────────────────
   NuevaReservaModal — turno de mostrador. Servicios → profesional → fecha → horario (GET /availability)
   y alta con POST /appointments. Nunca se manda correo ni DNI. La clave de idempotencia es por apertura.
───────────────────────────────────────── */

const MAX_SERVICIOS = 10;

const nuevaClave = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

/** Duration shown on the service chip: a range for services with variants. */
const duracionDelServicio = (s) => {
  if (!tieneVariantes(s)) return duracionLabel(s.durationMinutes);
  const { min, max } = rangoDeDuracion(s);
  return min === max ? duracionLabel(min) : `${min} a ${max} min`;
};

const rango = (valores, formato) => {
  const lista = valores.filter((v) => Number.isFinite(v));
  if (!lista.length) return "";
  const min = Math.min(...lista);
  const max = Math.max(...lista);
  return min === max ? formato(min) : `${formato(min)} a ${formato(max)}`;
};

/** Variant chips and add-on checks of one selected service (zr-line-config card). */
const ConfigDelServicio = ({ servicio, eleccion, onChange }) => {
  const alternarAdicional = (id) =>
    onChange({ addOnIds: eleccion.addOnIds.includes(id) ? eleccion.addOnIds.filter((x) => x !== id) : [...eleccion.addOnIds, id] });
  return (
    <div className="zr-line-config" role="group" aria-label={`Opciones de ${servicio.name}`}>
      <p className="zr-line-config__title">{servicio.name}</p>
      {tieneVariantes(servicio) && (
        <div className="ef-field">
          <span className="ef-label">Variante</span>
          <div className="ba-chips">
            {servicio.variants.map((v) => {
              const id = idDeVariante(v);
              return (
                <button
                  key={id}
                  type="button"
                  className="pu-mode"
                  style={{ padding: "6px 12px" }}
                  aria-pressed={eleccion.variantId === id}
                  aria-label={`${v.name}, ${v.durationMinutes} min, ${plata(v.price)}`}
                  onClick={() => onChange({ variantId: id })}
                >
                  {v.name}
                  <small>{v.durationMinutes} min · {plata(v.price)}</small>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {tieneAdicionales(servicio) && (
        <div className="ef-field">
          <span className="ef-label">Adicionales</span>
          {servicio.addOns.map((a) => {
            const id = idDeAdicional(a);
            return (
              <label key={id} className="zr-addon-check">
                <input type="checkbox" className="zr-check" checked={eleccion.addOnIds.includes(id)} onChange={() => alternarAdicional(id)} />
                {a.name}
                <small>+ {a.durationMinutes} min · + {plata(a.price)}</small>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

const NuevaReservaModal = ({ profesionales = [], inicial = {}, esEmpleado = false, onClose, onCreated, onSeriesCreated }) => {
  const activos = useMemo(() => profesionales.filter((p) => p.active !== false), [profesionales]);
  const unico = esEmpleado && activos.length === 1 ? activos[0].id : "";

  const [servicios, setServicios] = useState([]);
  const [cargandoServicios, setCargandoServicios] = useState(true);
  const [errorServicios, setErrorServicios] = useState("");
  // [{ serviceId, variantId, addOnIds }] in the order the services were picked; variantId stays "" until chosen.
  const [seleccion, setSeleccion] = useState([]);
  const [professionalId, setProfessionalId] = useState(inicial.professionalId || unico || "");
  const [date, setDate] = useState(inicial.date || dayjs().format(DATE_FORMAT));
  const [slotStart, setSlotStart] = useState(inicial.slotStart || "");
  const [disponibilidad, setDisponibilidad] = useState(null);
  const [cargandoHorarios, setCargandoHorarios] = useState(false);
  const [errorHorarios, setErrorHorarios] = useState("");
  const [name, setName] = useState("");
  const [countryCode, setCountryCode] = useState("AR");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneValid, setPhoneValid] = useState(false);
  const [notes, setNotes] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const claveRef = useRef(nuevaClave());
  const pedidoRef = useRef(0);
  const minDate = useMemo(() => dayjs().startOf("day"), []);
  const maxDate = useMemo(() => minDate.add(365, "day"), [minDate]);

  const cargarServicios = useCallback(async () => {
    setCargandoServicios(true);
    setErrorServicios("");
    try {
      const res = await beautyListServicesApi();
      const lista = Array.isArray(res?.data?.info) ? res.data.info : [];
      setServicios(lista.filter((s) => s.active !== false));
    } catch (e) {
      setErrorServicios(mensajeDe(e, "No pudimos cargar tus servicios. Probá de nuevo."));
    } finally {
      setCargandoServicios(false);
    }
  }, []);

  useEffect(() => {
    cargarServicios();
  }, [cargarServicios]);

  const serviceIds = useMemo(() => seleccion.map((s) => s.serviceId), [seleccion]);
  const completa = useMemo(() => seleccionCompleta(seleccion, servicios), [seleccion, servicios]);
  const items = useMemo(() => itemsDeSeleccion(seleccion), [seleccion]);
  const turnoFijo = useTurnoFijo({ items, professionalId, date, slotStart, completa });
  const fijo = turnoFijo.fixed;

  const cargarHorarios = useCallback(async () => {
    // Slots are asked for only once every service that has variants has its variant chosen.
    if (!seleccion.length || !completa || !date) {
      pedidoRef.current += 1;
      setDisponibilidad(null);
      setErrorHorarios("");
      setCargandoHorarios(false);
      return;
    }
    const pedido = ++pedidoRef.current;
    setCargandoHorarios(true);
    setErrorHorarios("");
    try {
      const res = await beautyAvailabilityApi({ date, items: itemsDeSeleccion(seleccion), professionalId: professionalId || undefined });
      if (pedido !== pedidoRef.current) return;
      const info = res?.data?.info || null;
      setDisponibilidad(info);
      const slots = Array.isArray(info?.slots) ? info.slots : [];
      setSlotStart((actual) => (slots.some((s) => s.slotStart === actual) ? actual : ""));
    } catch (e) {
      if (pedido !== pedidoRef.current) return;
      setDisponibilidad(null);
      setSlotStart("");
      setErrorHorarios(mensajeDeSeleccion(e) || mensajeDe(e, "No pudimos cargar los horarios. Probá de nuevo."));
    } finally {
      if (pedido === pedidoRef.current) setCargandoHorarios(false);
    }
  }, [seleccion, completa, professionalId, date]);

  useEffect(() => {
    cargarHorarios();
    return () => {
      pedidoRef.current += 1;
    };
  }, [cargarHorarios]);

  const elegidos = useMemo(() => servicios.filter((s) => serviceIds.includes(s._id)), [servicios, serviceIds]);
  const conOpciones = useMemo(
    () => seleccion.map((e) => ({ eleccion: e, servicio: servicios.find((s) => s._id === e.serviceId) })).filter((x) => tieneOpciones(x.servicio)),
    [seleccion, servicios]
  );
  const slots = Array.isArray(disponibilidad?.slots) ? disponibilidad.slots : [];

  // Totales: los del back (con los precios propios del profesional) o, mientras tanto, los del catálogo.
  const resumen = useMemo(() => {
    if (!elegidos.length) return null;
    if (disponibilidad?.mode === "specific") {
      return { duracion: duracionLabel(disponibilidad.totalDurationMinutes), precio: plata(disponibilidad.totalPrice) };
    }
    if (disponibilidad?.mode === "any" && Array.isArray(disponibilidad.professionals) && disponibilidad.professionals.length) {
      const pros = disponibilidad.professionals;
      return {
        duracion: rango(pros.map((p) => p.totalDurationMinutes), duracionLabel),
        precio: rango(pros.map((p) => p.totalPrice), plata),
      };
    }
    const totales = seleccion.map((e) => totalDeEleccion(servicios.find((s) => s._id === e.serviceId), e.variantId, e.addOnIds));
    return {
      duracion: duracionLabel(totales.reduce((t, x) => t + x.minutos, 0)),
      precio: plata(totales.reduce((t, x) => t + x.precio, 0)),
    };
  }, [elegidos, seleccion, servicios, disponibilidad]);

  // One preparation per appointment: the longest among the chosen services (the back keeps the same snapshot).
  const preparacion = useMemo(
    () => elegidos.reduce((max, s) => Math.max(max, Number(s.bufferAfterMinutes) || 0), 0),
    [elegidos]
  );
  const slotElegido = slots.find((s) => s.slotStart === slotStart);
  const finServicio = useMemo(() => {
    if (!slotElegido) return null;
    if (Number.isFinite(slotElegido.endMin)) return slotElegido.endMin;
    const inicio = toMin(slotElegido.slotStart);
    if (inicio == null) return null;
    if (disponibilidad?.mode === "specific") return inicio + Number(disponibilidad.totalDurationMinutes || 0);
    // "Sin preferencia": the end is known only when every candidate professional takes the same time.
    const duraciones = new Set(
      (disponibilidad?.professionals || [])
        .filter((p) => !slotElegido.professionalIds || slotElegido.professionalIds.includes(p.id))
        .map((p) => p.totalDurationMinutes)
    );
    return duraciones.size === 1 ? inicio + [...duraciones][0] : null;
  }, [slotElegido, disponibilidad]);

  const pais = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];
  const telefonoCargado = phoneNumber.trim().length > 0;
  // A turno fijo needs the phone (the back requires it) and at least one available date in the current preview.
  const puedeCrear =
    seleccion.length > 0 &&
    completa &&
    slotStart &&
    name.trim() &&
    (fijo ? telefonoCargado && phoneValid && turnoFijo.availableCount > 0 && !turnoFijo.rangeError : !telefonoCargado || phoneValid) &&
    !guardando;
  const profesionalElegido = activos.find((p) => p.id === professionalId);

  const crearSerie = async () => {
    setError("");
    turnoFijo.setSkipped(null);
    setGuardando(true);
    try {
      const res = await beautyCreateSeriesApi({
        professionalId,
        items,
        date,
        slotStart,
        intervalWeeks: turnoFijo.intervalWeeks,
        dateTo: turnoFijo.dateTo,
        name: name.trim(),
        phone: `${pais.prefix} ${phoneNumber.trim()}`,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      await (onSeriesCreated || onCreated)?.(res?.data?.info || null);
    } catch (e) {
      const data = e?.response?.data;
      setError(seriesErrorMessage(e, "No pudimos crear el turno fijo. Probá de nuevo."));
      // 409 "no dates": nothing was created; the form stays so the organizer can change the time or the end date.
      if (e?.response?.status === 409 && Array.isArray(data?.skipped)) turnoFijo.setSkipped(skippedEntries(data.skipped));
      setGuardando(false);
    }
  };

  const crear = async () => {
    if (!name.trim()) {
      setError("Falta el nombre de la persona");
      return;
    }
    if (fijo) return crearSerie();
    setError("");
    setGuardando(true);
    try {
      const body = {
        items: itemsDeSeleccion(seleccion),
        date,
        slotStart,
        name: name.trim(),
        ...(professionalId ? { professionalId } : {}),
        ...(telefonoCargado ? { phone: `${pais.prefix} ${phoneNumber.trim()}` } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      };
      const res = await beautyCreateAppointmentApi(body, claveRef.current);
      await onCreated?.(res?.data?.info || null);
    } catch (e) {
      setError(mensajeDeSeleccion(e) || mensajeDe(e, "No pudimos crear el turno. Probá de nuevo."));
      // Con respuesta del back el alta no quedó hecha: la próxima prueba usa otra clave.
      if (e?.response) claveRef.current = nuevaClave();
      if (e?.response?.status === 409) {
        setSlotStart("");
        cargarHorarios();
      }
      setGuardando(false);
    }
  };

  return (
    <div className="zr-overlay" onMouseDown={guardando ? undefined : onClose}>
      <div className="zr-modal zr-side-panel zr-srv-modal ba-modal" role="dialog" aria-modal="true" aria-label="Nueva reserva" onMouseDown={(e) => e.stopPropagation()}>
        <div className="zr-side-panel__head">
          <h3 className="zr-side-panel__title">Nueva reserva</h3>
          <button type="button" className="zr-btn zr-btn--ghost zr-side-panel__close" onClick={onClose} aria-label="Cerrar" disabled={guardando}>
            ×
          </button>
        </div>
        <div className="pu-body">
          {error && <div className="ef-alert" role="alert">{error}</div>}

          <Campo label="Servicios" hint={`Hasta ${MAX_SERVICIOS} por turno.`}>
            {cargandoServicios ? (
              <SpinLoading />
            ) : errorServicios ? (
              <div className="ef-alert" role="alert">
                {errorServicios}{" "}
                <button type="button" className="zr-btn zr-btn--ghost" onClick={cargarServicios}>Reintentar</button>
              </div>
            ) : !servicios.length ? (
              <p className="ba-aviso">No tenés servicios activos. Cargalos en la pestaña Servicios.</p>
            ) : (
              <div className="ba-chips">
                {servicios.map((s) => (
                  <Chip
                    key={s._id}
                    activo={serviceIds.includes(s._id)}
                    label={s.name}
                    onClick={() => {
                      if (serviceIds.includes(s._id)) return setSeleccion((prev) => prev.filter((e) => e.serviceId !== s._id));
                      if (serviceIds.length >= MAX_SERVICIOS) return;
                      setSeleccion((prev) => [...prev, { serviceId: s._id, variantId: "", addOnIds: [] }]);
                    }}
                  >
                    {s.name} · {duracionDelServicio(s)}
                  </Chip>
                ))}
              </div>
            )}
          </Campo>

          {conOpciones.map(({ eleccion, servicio }) => (
            <ConfigDelServicio
              key={servicio._id}
              servicio={servicio}
              eleccion={eleccion}
              onChange={(patch) =>
                setSeleccion((prev) => prev.map((e) => (e.serviceId === servicio._id ? { ...e, ...patch } : e)))
              }
            />
          ))}

          <div className="pu-grid-2">
            <Campo label="Profesional">
              <select
                className="ef-input"
                aria-label="Profesional"
                value={professionalId}
                onChange={(e) => setProfessionalId(e.target.value)}
              >
                {!esEmpleado && <option value="">Sin preferencia</option>}
                {activos.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </Campo>
            <Campo label="Fecha">
              <SingleDatePickerPill
                value={dayjs(date)}
                onChange={(d) => d && d.isValid() && setDate(d.format(DATE_FORMAT))}
                minDate={minDate}
                maxDate={maxDate}
                ariaLabel="Fecha"
              />
            </Campo>
          </div>

          <Campo label="Horario">
            {!serviceIds.length ? (
              <p className="ba-aviso">Elegí al menos un servicio para ver los horarios.</p>
            ) : !completa ? (
              <p className="ba-aviso">Elegí la variante de cada servicio para ver los horarios.</p>
            ) : cargandoHorarios ? (
              <p className="ba-aviso" role="status">Buscando horarios...</p>
            ) : errorHorarios ? (
              <div className="ef-alert" role="alert">
                {errorHorarios}{" "}
                <button type="button" className="zr-btn zr-btn--ghost" onClick={cargarHorarios}>Reintentar</button>
              </div>
            ) : !slots.length ? (
              <p className="ba-aviso">No hay horarios libres ese día. Probá con otra fecha o profesional.</p>
            ) : (
              <div className="ba-chips">
                {slots.map((s) => (
                  <Chip key={s.slotStart} activo={slotStart === s.slotStart} label={`Horario ${s.slotStart}`} onClick={() => setSlotStart(s.slotStart)}>
                    {s.slotStart}
                  </Chip>
                ))}
              </div>
            )}
          </Campo>

          {resumen && (
            <div className="ba-resumen" aria-label="Resumen">
              <span>Duración: {resumen.duracion}</span>
              <span>Total: {resumen.precio}</span>
              {preparacion > 0 && slotElegido && (
                <span className="ba-resumen__prep">
                  {finServicio != null
                    ? `${slotStart} a ${toHHmm(finServicio)}, y ${preparacion} min de preparación hasta las ${toHHmm(finServicio + preparacion)}`
                    : `y ${preparacion} min de preparación`}
                </span>
              )}
            </div>
          )}

          <TurnoFijoBlock
            ctl={turnoFijo}
            date={date}
            slotStart={slotStart}
            professionalId={professionalId}
            professionalName={profesionalElegido?.name || ""}
          />

          <Campo label="Nombre">
            <input className="ef-input" aria-label="Nombre" placeholder="Nombre y apellido" value={name} onChange={(e) => setName(e.target.value)} />
          </Campo>
          <Campo label={fijo ? "Teléfono" : "Teléfono (opcional)"} hint={fijo ? "Un turno fijo necesita el teléfono de la persona." : undefined}>
            <PhoneCountryInput
              countryCode={countryCode}
              number={phoneNumber}
              error={telefonoCargado && !phoneValid}
              onChange={({ countryCode: cc, number, valid }) => {
                setCountryCode(cc);
                setPhoneNumber(number);
                setPhoneValid(valid);
              }}
            />
            {telefonoCargado && !phoneValid && <small style={{ color: "#e53935" }}>Este número no es válido</small>}
          </Campo>
          <Campo label="Notas (opcional)">
            <input className="ef-input" aria-label="Notas" placeholder="Observaciones" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Campo>
        </div>
        <div className="zr-side-panel__foot">
          <button type="button" className="zr-btn zr-btn--ghost" onClick={onClose} disabled={guardando}>Cancelar</button>
          <button type="button" className="zr-btn zr-btn--dark" onClick={crear} disabled={!puedeCrear}>
            {guardando
              ? "Creando..."
              : fijo
              ? turnoFijo.availableCount > 0
                ? `Crear ${pluralTurnos(turnoFijo.availableCount)}`
                : "Crear turno fijo"
              : "Crear reserva"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NuevaReservaModal;
